import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, unref as _unref, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col gap-2 text-left",
  "data-testid": "post-station-frozen-records"
}
const _hoisted_2 = { class: "flex items-baseline justify-between gap-2" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("p", { class: "text-xs font-semibold uppercase tracking-wide text-neutral50" }, " Registros recebidos depois do fechamento ", -1)
const _hoisted_4 = { class: "text-xs text-neutral50" }
const _hoisted_5 = { class: "w-full overflow-x-auto" }
const _hoisted_6 = { class: "w-full min-w-[560px] text-left text-xs" }
const _hoisted_7 = { class: "text-neutral50" }
const _hoisted_8 = { class: "divide-y divide-gray-200" }
const _hoisted_9 = ["data-testid"]
const _hoisted_10 = { class: "py-2 pr-3" }
const _hoisted_11 = { class: "flex flex-col gap-0.5" }
const _hoisted_12 = { class: "text-textPrimary" }
const _hoisted_13 = { class: "text-neutral50" }
const _hoisted_14 = ["data-testid", "onClick"]
const _hoisted_15 = { class: "py-2 pr-3 text-textPrimary" }
const _hoisted_16 = { class: "py-2 pr-3 text-textPrimary" }
const _hoisted_17 = { class: "py-2 pr-3 text-textPrimary" }
const _hoisted_18 = ["data-testid"]

import { computed } from 'vue'

import {
  COVERED_LEGS_LABELS,
  CoveredLegs,
  DS_ATTENDANCE_STATUS_LABELS,
  DSAttendanceStatus
} from '@/constants/ds-attendance.constants'
import type { StationRegistrationActivityRecord } from '@/Network/Services/StationService'
import { formatDayMonthInOffset, formatHourMinuteInOffset } from '@/utils/date'

import { FROZEN_RECORD_EFFECT_TEXT } from '../constants'

/**
 * Q28 / M5 seção 02 — "Registros recebidos depois do fechamento".
 *
 * Aprovar o pós-plantão congela o horário efetivo. Os registros que chegarem
 * depois continuam existindo, continuam sendo auditados normalmente e aparecem
 * AQUI, com o carimbo de que não mexem no número que foi para pagamento. Quem
 * quiser incorporá-los reabre o pós-plantão (reversão, com a permissão que já
 * existe) — esta tabela só mostra, não decide.
 */
const ACTIVITY_RECORD_TITLE = 'Registro de Atividade · Jornada'

const DISPLAY_FALLBACK = '—'


export default /*#__PURE__*/_defineComponent({
  __name: 'PostStationFrozenRecordsTable',
  props: {
    records: {},
    timezone: {}
  },
  emits: ["view-record"],
  setup(__props: any, { emit }) {

const props = __props;





const HEADERS = ['Registro', 'Perna', 'Horário declarado', 'Auditoria', 'Efeito']

const timezone = computed(() => props.timezone || '')

const formatHour = (iso?: string | null): string =>
  (iso && formatHourMinuteInOffset(iso, timezone.value)) || DISPLAY_FALLBACK

const formatDay = (iso?: string | null): string | null =>
  (iso && formatDayMonthInOffset(iso, timezone.value)) || null

// O horário declarado é o da perna que o registro cobre; `BOTH` mostra os dois.
const declaredFor = (record: StationRegistrationActivityRecord): string => {
  if (record.coveredLegs === CoveredLegs.CHECKIN) return formatHour(record.declaredCheckin)
  if (record.coveredLegs === CoveredLegs.CHECKOUT) return formatHour(record.declaredCheckout)
  const checkin = formatHour(record.declaredCheckin)
  const checkout = formatHour(record.declaredCheckout)
  if (checkin === DISPLAY_FALLBACK && checkout === DISPLAY_FALLBACK) return DISPLAY_FALLBACK
  return `${checkin} → ${checkout}`
}

// Status cru desconhecido cai no próprio código em vez de sumir: o auditor
// precisa saber que existe algo ali que esta versão da tela não sabe nomear.
const auditLabelFor = (record: StationRegistrationActivityRecord): string => {
  const statusLabel =
    DS_ATTENDANCE_STATUS_LABELS[record.status as DSAttendanceStatus] ?? record.status
  const day = formatDay(record.reviewedAt)
  return day ? `${statusLabel} em ${day}` : statusLabel
}

const subtitleFor = (record: StationRegistrationActivityRecord): string => {
  const attachments = record.attachments?.length ?? 0
  const parts: string[] = []
  if (attachments > 0) parts.push(`${attachments} ${attachments === 1 ? 'anexo' : 'anexos'}`)
  if (record.activityTypeLabel) parts.push(record.activityTypeLabel)
  return parts.length ? parts.join(' · ') : DISPLAY_FALLBACK
}

const rows = computed(() =>
  props.records.map((record) => ({
    containerId: record.containerId,
    subtitle: subtitleFor(record),
    legLabel: record.coveredLegs
      ? (COVERED_LEGS_LABELS[record.coveredLegs as CoveredLegs] ?? record.coveredLegs)
      : DISPLAY_FALLBACK,
    declaredAt: declaredFor(record),
    auditLabel: auditLabelFor(record)
  }))
)

const itemWord = computed(() => (rows.value.length === 1 ? 'item' : 'itens'))

return (_ctx: any,_cache: any) => {
  return (rows.value.length)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _hoisted_3,
          _createElementVNode("span", _hoisted_4, _toDisplayString(rows.value.length) + " " + _toDisplayString(itemWord.value), 1)
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("table", _hoisted_6, [
            _createElementVNode("thead", null, [
              _createElementVNode("tr", _hoisted_7, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(HEADERS, (header) => {
                  return _createElementVNode("th", {
                    key: header,
                    class: "font-semibold py-2 pr-3"
                  }, _toDisplayString(header), 1)
                }), 64))
              ])
            ]),
            _createElementVNode("tbody", _hoisted_8, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (row) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: row.containerId,
                  "data-testid": `post-station-frozen-record-${row.containerId}`,
                  class: "align-top"
                }, [
                  _createElementVNode("td", _hoisted_10, [
                    _createElementVNode("div", _hoisted_11, [
                      _createElementVNode("span", _hoisted_12, [
                        _createTextVNode(_toDisplayString(ACTIVITY_RECORD_TITLE) + " "),
                        _createElementVNode("strong", null, "#" + _toDisplayString(row.containerId), 1)
                      ]),
                      _createElementVNode("span", _hoisted_13, _toDisplayString(row.subtitle), 1),
                      _createElementVNode("button", {
                        type: "button",
                        class: "self-start text-primary30 underline",
                        "data-testid": `post-station-frozen-record-${row.containerId}-view`,
                        onClick: ($event: any) => (emit('view-record', row.containerId))
                      }, " Ver registro ", 8, _hoisted_14)
                    ])
                  ]),
                  _createElementVNode("td", _hoisted_15, _toDisplayString(row.legLabel), 1),
                  _createElementVNode("td", _hoisted_16, _toDisplayString(row.declaredAt), 1),
                  _createElementVNode("td", _hoisted_17, _toDisplayString(row.auditLabel), 1),
                  _createElementVNode("td", {
                    class: "py-2 text-neutral50",
                    "data-testid": `post-station-frozen-record-effect-${row.containerId}`
                  }, _toDisplayString(_unref(FROZEN_RECORD_EFFECT_TEXT)), 9, _hoisted_18)
                ], 8, _hoisted_9))
              }), 128))
            ])
          ])
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})